import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, Download, FileArrowDown, Key, PencilSimple, Plus, Trash, UploadSimple } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import WrmSelect from "../../components/WrmSelect";
import { PasswordField, PasswordStrength, passwordStrong } from "../../components/PasswordFields";
import { INVESTOR_TYPES, INVESTOR_TYPE_NAMES, TENURE_OPTIONS, WITHDRAWAL_OPTIONS, RANGE_DISCLAIMER } from "../../lib/plans";
import { downloadDoc, downloadStatement } from "../../lib/download";
import { inr, pct, fmtDate, fmtDateTime, gainClass, kycBadge, riskBadge, clientStatusBadge, txnBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import StatCard from "../../components/ui/StatCard";
import SecureAvatar from "../../components/SecureAvatar";
import WithdrawalPanel from "../../components/WithdrawalPanel";
import TxnEditDialog from "../../components/TxnEditDialog";
import { AllocationDonut, GrowthArea } from "../../components/charts";
import { PeriodSelector, historyToChart } from "../../components/period";
import TxnPeriodFilter, { periodRange, inRange } from "../../components/TxnPeriodFilter";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";

const TABS = ["Overview", "Personal", "KYC", "Portfolio", "Holdings", "Transactions",
  "Performance", "Documents", "Nominee", "Investment Plan", "Valuations", "Notes", "Activity"];
const KYC_STATUSES = ["Pending", "Submitted", "Under Review", "Verified", "Rejected"];
const KYC_ITEM_STATUSES = ["Pending", "Verified", "Rejected", "Not Applicable"];
const RISKS = ["Conservative", "Balanced", "Experienced"];
const CLIENT_STATUSES = ["Active", "Inactive", "On Hold", "Closed"];
const ASSET_CLASSES = ["Equity", "ETF", "Bonds & Fixed Income", "Derivatives",
  "Commodities & Alternatives", "Private Funds", "Cash / Current Investments", "Other"];
const TXN_TYPES = ["Investment", "Additional Investment", "Withdrawal", "Redemption",
  "Dividend", "Interest", "Fee", "Other"];
const DOC_CATEGORIES = ["KYC Documents", "Agreements", "Statements", "Transaction Documents",
  "Bank Proof", "Financial Statement", "Other Documents"];

const In = ({ label, value, onChange, type = "text", testId }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <input type={type} value={value ?? ""} data-testid={testId} onChange={(e) => onChange(e.target.value)} className="glass-input" />
  </div>
);

const Sel = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
      {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
    </select>
  </div>
);

const SaveBar = ({ onSave, saving, testId }) => (
  <div className="mt-6 flex justify-end">
    <Button onClick={onSave} disabled={saving} data-testid={testId}
      className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
      {saving ? "Saving..." : "Save Changes"}
    </Button>
  </div>
);

function CompletionBar({ value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="label-caps">KYC Completion</p>
        <p className="tnum text-sm font-semibold text-emerald-300" data-testid={`${testId}-value`}>{value}%</p>
      </div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-2 rounded-full bg-emerald-500 transition-[width] duration-500 emerald-glow" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("Overview");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [holdingDlg, setHoldingDlg] = useState(null);
  const [txnDlg, setTxnDlg] = useState(false);
  const [valDlg, setValDlg] = useState(null);
  const [editTxn, setEditTxn] = useState(null);
  const [uploadCat, setUploadCat] = useState("KYC Documents");
  const [codeDlg, setCodeDlg] = useState(false);
  const [codeValue, setCodeValue] = useState("");
  const [kycEdit, setKycEdit] = useState({});
  const [ovPeriod, setOvPeriod] = useState("ALL");
  const [txnMode, setTxnMode] = useState("All");
  const [txnFrom, setTxnFrom] = useState("");
  const [txnTo, setTxnTo] = useState("");
  const [pmPeriod, setPmPeriod] = useState("ALL");
  const [photoKey, setPhotoKey] = useState(0);
  const [pwDlg, setPwDlg] = useState(null);
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  const load = () => {
    api.get(`/admin/investors/${id}`).then((r) => {
      setData(r.data);
      const p = r.data.profile;
      setCodeValue(p.investor_code);
      const kycInit = {};
      (r.data.kyc_records || []).forEach((k) => {
        kycInit[k.id] = { status: k.status, remarks: k.remarks || "", verified_at: k.verified_at || "" };
      });
      setKycEdit(kycInit);
      setForm({
        full_name: p.full_name, dob: p.dob || "", gender: p.gender || "", email: p.email,
        username: p.username || "",
        mobile: p.mobile || "", address: p.address || "", city: p.city || "", state: p.state || "",
        country: p.country || "India", client_status: p.client_status || "Active",
        pan: p.pan || "", aadhaar: p.aadhaar || "", kyc_status: p.kyc_status,
        kyc_verified_at: p.kyc_verified_at || "", kyc_remarks: p.kyc_remarks || "",
        investor_type: p.investor_type || "", tenure_category: p.tenure_category || "",
        investment_objective: p.investment_objective || "",
        withdrawal_preference: p.withdrawal_preference || "",
        exit_load_pct: p.exit_load_pct ?? "", profit_sharing_pct: p.profit_sharing_pct ?? "",
        capital_lock_days: p.capital_lock_days ?? "",
        allow_partial_withdrawal: p.allow_partial_withdrawal == null ? "" : (p.allow_partial_withdrawal ? "Yes" : "No"),
        allow_full_redemption: p.allow_full_redemption == null ? "" : (p.allow_full_redemption ? "Yes" : "No"),
        nominee: {
          name: p.nominee?.name || "", relation: p.nominee?.relation || "", dob: p.nominee?.dob || "",
          mobile: p.nominee?.mobile || "", email: p.nominee?.email || "", share_pct: p.nominee?.share_pct ?? 100,
        },
        wrm_user_id: p.wrm_user_id || "", region: p.region || "", channel: p.channel || "",
        relationship_status: p.relationship_status || "Active",
        notes: {
          internal: p.notes?.internal || "", preferences: p.notes?.preferences || "",
          operational: p.notes?.operational || "", special_instructions: p.notes?.special_instructions || "",
        },
      });
    }).catch((e) => setError(apiError(e)));
    api.get(`/admin/investors/${id}/activity`).then((r) => setActivity(r.data)).catch(() => {});
  };
  useEffect(load, [id]);

  const save = async (fields, successMsg = "Client record updated — reflected across all portals.") => {
    setSaving(true);
    try {
      await api.patch(`/admin/investors/${id}`, fields);
      toast.success(successMsg);
      load();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const txnStmtQuery = () => {
    const [f, t] = periodRange(txnMode, txnFrom, txnTo);
    return f || t ? `?from_date=${f || ""}&to_date=${t || ""}` : "";
  };
  const applyTxnPeriod = () => {
    if (txnMode === "Custom") {
      if (!txnFrom || !txnTo) { toast.error("Select both From and To dates."); return; }
      if (txnTo < txnFrom) { toast.error("To Date cannot be earlier than From Date."); return; }
    }
  };

  const resetPassword = async () => {
    if (!passwordStrong(pwDlg.password) || pwDlg.password !== pwDlg.confirm) {
      toast.error("Enter a strong matching password (8+ chars, at least one letter and number).");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/users/${data.profile.user_id}/reset-password`, { new_password: pwDlg.password });
      toast.success("Client password updated — audit logged.");
      setPwDlg(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };
  const setNom = (k) => (v) => setForm((p) => ({ ...p, nominee: { ...p.nominee, [k]: v } }));
  const setNote = (k) => (v) => setForm((p) => ({ ...p, notes: { ...p.notes, [k]: v } }));

  const saveCode = async () => {
    const ok = await save({ investor_code: codeValue }, "Client Code updated");
    if (ok) setCodeDlg(false);
  };

  const saveKycItem = async (recordId) => {
    try {
      await api.patch(`/admin/kyc-records/${recordId}`, {
        status: kycEdit[recordId].status,
        remarks: kycEdit[recordId].remarks,
        verified_at: kycEdit[recordId].verified_at || null,
      });
      toast.success("Checklist item updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/investors/${id}/photo`, fd);
      toast.success("Client photo updated");
      setPhotoKey((k) => k + 1);
      load();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove client photo?")) return;
    try {
      await api.delete(`/admin/investors/${id}/photo`);
      toast.success("Photo removed");
      setPhotoKey((k) => k + 1);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveHolding = async (h) => {
    try {
      if (h.id) await api.patch(`/admin/holdings/${h.id}`, h);
      else await api.post(`/admin/investors/${id}/holdings`, h);
      toast.success(h.id ? "Holding updated" : "Holding added");
      setHoldingDlg(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteHolding = async (h) => {
    if (!window.confirm(`Remove holding "${h.asset_name}"? This updates portfolio metrics immediately.`)) return;
    try {
      await api.delete(`/admin/holdings/${h.id}`);
      toast.success("Holding removed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveTxn = async (t, purchaseCharge) => {
    try {
      await api.post(`/admin/investors/${id}/transactions`, t);
      if (purchaseCharge) {
        await api.post(`/admin/investors/${id}/transactions`, {
          date: t.date, type: "Fee",
          description: `Purchase charge on additional purchase of ₹${Number(t.amount).toLocaleString("en-IN")}`,
          amount: purchaseCharge, status: t.status || "Completed", fee_type: "Purchase Charge",
        });
      }
      toast.success(purchaseCharge ? "Transaction + purchase charge recorded" : "Transaction recorded");
      setTxnDlg(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveValuation = async (v) => {
    const period = v.period || "monthly";
    if (period === "monthly" && !v.month) { toast.error("Month is required."); return; }
    if (period === "weekly" && !v.date) { toast.error("Valuation date is required for weekly entries."); return; }
    if (v.value === "" || Number(v.value) < 0) { toast.error("Closing value is required."); return; }
    const numOrNull = (x) => (x === "" || x === null || x === undefined ? null : Number(x));
    const payload = { month: v.month || (v.date ? v.date.slice(0, 7) : null), period, date: v.date || null, value: Number(v.value),
      opening_value: numOrNull(v.opening_value), investment_added: numOrNull(v.investment_added),
      withdrawal: numOrNull(v.withdrawal), notes: v.notes || null };
    try {
      if (v.id) {
        await api.patch(`/admin/valuations/${v.id}`, payload);
        toast.success("Valuation updated — graphs refreshed across all portals.");
      } else {
        await api.post(`/admin/investors/${id}/valuations`, payload);
        toast.success("Valuation recorded — performance graph updated.");
      }
      setValDlg(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeValuation = async (v) => {
    if (!window.confirm(`Delete valuation for ${v.month}? This is audit logged.`)) return;
    try {
      await api.delete(`/admin/valuations/${v.id}`);
      toast.success("Valuation removed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", uploadCat);
    try {
      await api.post(`/admin/investors/${id}/documents`, fd);
      toast.success("Document uploaded to secure storage");
      load();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const deleteDoc = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try {
      await api.delete(`/admin/documents/${d.id}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <AppLayout title="Client"><p className="text-red-400 text-sm" data-testid="detail-error">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Client"><PageLoader /></AppLayout>;

  const { profile, metrics, allocation, holdings, transactions, kyc_records, kyc_completion, documents, history, wrm, withdrawal } = data;
  const ovData = historyToChart(history, ovPeriod);
  const pmData = historyToChart(history, pmPeriod);

  return (
    <AppLayout title={profile.full_name}>
      <div className="fade-up space-y-6">
        <button onClick={() => navigate("/admin/clients")} data-testid="back-to-clients"
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-200">
          <ArrowLeft size={15} /> Back to Clients
        </button>

        <div className="glass-card p-6 md:p-7" data-testid="client-header">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="relative group">
                <SecureAvatar key={photoKey} investorId={id} name={profile.full_name} size={56} testId="client-photo" />
                <input ref={photoRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                  data-testid="photo-upload-input" onChange={uploadPhoto} />
                <button onClick={() => photoRef.current?.click()} data-testid="photo-upload-button"
                  className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-emerald-500 text-[#030706] flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity duration-200"
                  title="Upload / replace photo">
                  <Camera size={12} weight="bold" />
                </button>
              </div>
              <div>
                <h2 className="font-display text-2xl text-white">{profile.full_name}</h2>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <p className="tnum text-xs text-zinc-500" data-testid="header-client-code">{profile.investor_code}</p>
                  <button onClick={() => setCodeDlg(true)} data-testid="edit-client-code"
                    className="text-zinc-600 hover:text-emerald-300 transition-colors duration-200" title="Edit Client Code">
                    <PencilSimple size={12} />
                  </button>
                </div>
                {profile.photo_path && (
                  <button onClick={removePhoto} data-testid="photo-remove-button"
                    className="text-[10px] text-zinc-600 hover:text-red-400 transition-colors duration-200 mt-0.5">
                    Remove photo
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 ml-auto">
              <div><p className="label-caps">AUM</p><p className="tnum text-lg text-white mt-1" data-testid="header-aum">{inr(metrics.current_value)}</p></div>
              <div><p className="label-caps">Risk</p><Badge variant="outline" className={`${riskBadge(profile.risk_profile)} text-[10px] mt-1`}>{profile.risk_profile}</Badge></div>
              <div><p className="label-caps">KYC</p><Badge variant="outline" className={`${kycBadge(profile.kyc_status)} text-[10px] mt-1`} data-testid="header-kyc">{profile.kyc_status}</Badge></div>
              <div><p className="label-caps">Status</p><Badge variant="outline" className={`${clientStatusBadge(profile.client_status)} text-[10px] mt-1`}>{profile.client_status || "Active"}</Badge></div>
              <div><p className="label-caps">WRM</p><p className="text-sm text-zinc-200 mt-1">{wrm?.name || "Unassigned"}</p></div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="client-tabs">
          {TABS.map((t) => (
            <button key={t} data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTab(t)}
              className={`text-xs font-semibold rounded-full px-4 py-2 border whitespace-nowrap transition-colors duration-200 ${
                tab === t ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
              }`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && (
          <div className="space-y-5" data-testid="panel-overview">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
              <StatCard label="Invested" value={inr(metrics.total_invested)} testId="ov-invested" />
              <StatCard label="Current Value" value={inr(metrics.current_value)} testId="ov-current" />
              <StatCard label="Gain / Loss" value={inr(metrics.gain_loss)} delta={metrics.gain_loss_pct} testId="ov-gain" />
              <StatCard label="CAGR" value={metrics.cagr !== null ? pct(metrics.cagr) : "—"} testId="ov-cagr" />
              <StatCard label="XIRR" value={metrics.xirr !== null ? pct(metrics.xirr) : "—"} testId="ov-xirr" />
              <StatCard label="Total Withdrawals" value={inr(metrics.total_withdrawals || 0)}
                sub={`Capital ${inr(metrics.capital_withdrawn || 0)} · Returns ${inr(metrics.return_withdrawn || 0)}`}
                testId="ov-total-withdrawals" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              <div className="lg:col-span-3 glass-card p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h3 className="font-heading font-semibold text-white">Portfolio Growth</h3>
                  <PeriodSelector period={ovPeriod} setPeriod={setOvPeriod} testId="ov-period-selector" />
                </div>
                {ovData.length > 1 ? (
                  <GrowthArea data={ovData} height={260} testId="ov-growth-chart" />
                ) : (
                  <EmptyState message="Insufficient history for this range. Try a longer period." />
                )}
              </div>
              <div className="lg:col-span-2 glass-card p-6">
                <h3 className="font-heading font-semibold text-white mb-4">Allocation</h3>
                <AllocationDonut data={allocation} height={260} testId="ov-allocation-chart" />
              </div>
            </div>
            <WithdrawalPanel w={withdrawal} testId="ov-withdrawal" />
          </div>
        )}

        {tab === "Personal" && (
          <div className="glass-card p-7" data-testid="panel-personal">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <In label="Client Code" value={codeValue} onChange={setCodeValue} testId="e-client-code" />
              <In label="Login User ID" value={form.username} onChange={set("username")} testId="e-username" />
              <In label="Full Name" value={form.full_name} onChange={set("full_name")} testId="e-full-name" />
              <In label="Email" value={form.email} onChange={set("email")} testId="e-email" />
              <In label="Mobile" value={form.mobile} onChange={set("mobile")} testId="e-mobile" />
              <In label="Date of Birth" type="date" value={form.dob} onChange={set("dob")} testId="e-dob" />
              <Sel label="Gender" value={form.gender} onChange={set("gender")} options={["", "Male", "Female", "Other"]} testId="e-gender" />
              <Sel label="Client Status" value={form.client_status} onChange={set("client_status")} options={CLIENT_STATUSES} testId="e-client-status" />
              <In label="Address" value={form.address} onChange={set("address")} testId="e-address" />
              <In label="City" value={form.city} onChange={set("city")} testId="e-city" />
              <In label="State" value={form.state} onChange={set("state")} testId="e-state" />
              <In label="Country" value={form.country} onChange={set("country")} testId="e-country" />
              <WrmSelect value={form.wrm_user_id} onChange={set("wrm_user_id")} testId="e-wrm" />
              <In label="Region" value={form.region} onChange={set("region")} testId="e-region" />
              <In label="Channel" value={form.channel} onChange={set("channel")} testId="e-channel" />
              <Sel label="Relationship Status" value={form.relationship_status} onChange={set("relationship_status")}
                options={["Active", "Inactive", "On Hold"]} testId="e-rel-status" />
            </div>
            <div className="mt-6 pt-5 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="label-caps">Login Credentials</p>
                <p className="text-xs text-zinc-500 mt-1.5">Client signs in with Login ID or email. Passwords are stored hashed and never displayed.</p>
              </div>
              <Button variant="outline" data-testid="reset-password-btn" onClick={() => setPwDlg({ password: "", confirm: "" })}
                className="border-white/10 text-zinc-300 hover:bg-white/5">
                <Key size={14} className="mr-1.5" /> Reset Password
              </Button>
            </div>
            <SaveBar testId="save-personal" saving={saving} onSave={() => save({
              investor_code: codeValue, ...(form.username ? { username: form.username } : {}),
              full_name: form.full_name, email: form.email, mobile: form.mobile,
              dob: form.dob || null, gender: form.gender || null, address: form.address, city: form.city,
              state: form.state, country: form.country, client_status: form.client_status,
              wrm_user_id: form.wrm_user_id || null, region: form.region, channel: form.channel,
              relationship_status: form.relationship_status,
            })} />
          </div>
        )}

        <Dialog open={!!pwDlg} onOpenChange={() => setPwDlg(null)}>
          <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-md" data-testid="client-reset-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-white">Reset Client Password</DialogTitle>
              <DialogDescription className="text-zinc-500">
                Sets a new portal password for {form.full_name}. The current password is never shown.
              </DialogDescription>
            </DialogHeader>
            {pwDlg && (
              <div className="space-y-4">
                <PasswordField label="New Password" value={pwDlg.password} onChange={(v) => setPwDlg({ ...pwDlg, password: v })} testId="rp-password" />
                <PasswordField label="Confirm Password" value={pwDlg.confirm} onChange={(v) => setPwDlg({ ...pwDlg, confirm: v })} testId="rp-confirm" />
                <PasswordStrength password={pwDlg.password} testId="rp-strength" />
                {pwDlg.confirm && pwDlg.password !== pwDlg.confirm && (
                  <p className="text-[11px] text-red-400" data-testid="rp-mismatch">Passwords do not match.</p>
                )}
              </div>
            )}
            <Button onClick={resetPassword} disabled={saving} data-testid="rp-submit"
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold mt-2">
              {saving ? "Updating..." : "Reset Password"}
            </Button>
          </DialogContent>
        </Dialog>

        {tab === "KYC" && (
          <div className="space-y-5" data-testid="panel-kyc">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="glass-card p-6">
                <h3 className="font-heading font-semibold text-white mb-5">KYC Master Data</h3>
                <div className="space-y-4">
                  <In label="PAN" value={form.pan} onChange={set("pan")} testId="e-pan" />
                  <In label="Aadhaar" value={form.aadhaar} onChange={set("aadhaar")} testId="e-aadhaar" />
                  <Sel label="KYC Status" value={form.kyc_status} onChange={set("kyc_status")} options={KYC_STATUSES} testId="e-kyc-status" />
                  <In label="Verification Date" type="date" value={form.kyc_verified_at} onChange={set("kyc_verified_at")} testId="e-kyc-date" />
                  <In label="KYC Remarks" value={form.kyc_remarks} onChange={set("kyc_remarks")} testId="e-kyc-remarks" />
                </div>
                <SaveBar testId="save-kyc" saving={saving} onSave={() => save({
                  pan: form.pan, aadhaar: form.aadhaar, kyc_status: form.kyc_status,
                  kyc_verified_at: form.kyc_verified_at || null, kyc_remarks: form.kyc_remarks,
                })} />
              </div>
              <div className="lg:col-span-2 glass-card p-6">
                <CompletionBar value={kyc_completion ?? 0} testId="kyc-completion" />
                <h3 className="font-heading font-semibold text-white mt-6 mb-4">Verification Checklist</h3>
                <div className="space-y-3" data-testid="kyc-checklist">
                  {kyc_records.map((k) => (
                    <div key={k.id} className="border border-white/[0.06] bg-white/[0.02] rounded-lg p-4" data-testid={`kyc-item-${k.id}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-medium text-zinc-100 flex-1 min-w-[160px]">{k.document_type}</span>
                        <select value={kycEdit[k.id]?.status ?? k.status} data-testid={`kyc-item-status-${k.id}`}
                          onChange={(e) => setKycEdit((p) => ({ ...p, [k.id]: { ...p[k.id], status: e.target.value } }))}
                          className="glass-input h-9 w-40 text-xs appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                          {KYC_ITEM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <input type="date" value={kycEdit[k.id]?.verified_at ?? ""} data-testid={`kyc-item-date-${k.id}`}
                          onChange={(e) => setKycEdit((p) => ({ ...p, [k.id]: { ...p[k.id], verified_at: e.target.value } }))}
                          className="glass-input h-9 w-36 text-xs" />
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-3">
                        <input value={kycEdit[k.id]?.remarks ?? ""} placeholder="Verification remarks..."
                          data-testid={`kyc-item-remarks-${k.id}`}
                          onChange={(e) => setKycEdit((p) => ({ ...p, [k.id]: { ...p[k.id], remarks: e.target.value } }))}
                          className="glass-input h-9 flex-1 min-w-[200px] text-xs" />
                        <Button size="sm" variant="outline" data-testid={`kyc-item-save-${k.id}`}
                          onClick={() => saveKycItem(k.id)}
                          className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 h-9">
                          Save
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "Portfolio" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="panel-portfolio">
            <div className="glass-card p-6">
              <h3 className="font-heading font-semibold text-white mb-4">Asset Allocation</h3>
              <AllocationDonut data={allocation} height={280} testId="pf-donut" />
            </div>
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading font-semibold text-white">Portfolio Summary</h3>
                <Button size="sm" variant="outline" data-testid="admin-portfolio-statement"
                  onClick={() => downloadStatement(`/statements/${id}/portfolio.pdf`, `KMC-Portfolio-Statement-${profile.investor_code}.pdf`)}
                  className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
                  <FileArrowDown size={14} className="mr-1" /> Statement (PDF)
                </Button>
              </div>
              <div className="space-y-4" data-testid="portfolio-summary">
                {[["Invested Value", inr(metrics.total_invested)], ["Current Value", inr(metrics.current_value)],
                  ["Absolute Return", `${inr(metrics.gain_loss)} (${pct(metrics.gain_loss_pct)})`],
                  ["CAGR", metrics.cagr !== null ? pct(metrics.cagr) : "—"],
                  ["XIRR", metrics.xirr !== null ? pct(metrics.xirr) : "—"],
                  ["Positions", holdings.length]].map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between border-b border-white/[0.04] pb-3">
                    <span className="label-caps">{l}</span>
                    <span className="tnum text-sm font-medium text-white">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "Holdings" && (
          <div className="glass-card overflow-hidden" data-testid="panel-holdings">
            <div className="px-6 pt-5 pb-4 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-white">Portfolio Holdings</h3>
              <Button size="sm" data-testid="add-holding-button" onClick={() => setHoldingDlg({ asset_name: "", asset_class: "Equity", investment_date: "", invested_amount: "", units: "", purchase_price: "", current_price: "", current_value: "", status: "Active" })}
                className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
                <Plus size={14} className="mr-1" weight="bold" /> Add Holding
              </Button>
            </div>
            {holdings.length === 0 ? <EmptyState message="No holdings yet. Add the first position." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-holdings-table">
                  <thead>
                    <tr className="border-y border-white/[0.06]">
                      <th className={thCls}>Asset</th>
                      <th className={thCls}>Class</th>
                      <th className={`${thCls} text-right`}>Invested</th>
                      <th className={`${thCls} text-right`}>Current</th>
                      <th className={`${thCls} text-right`}>Return</th>
                      <th className={`${thCls} text-right`}>Alloc %</th>
                      <th className={thCls}>Status</th>
                      <th className={`${thCls} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holdings.map((h) => (
                      <tr key={h.id} className={rowCls}>
                        <td className={`${tdCls} font-medium text-white`}>{h.asset_name}</td>
                        <td className={`${tdCls} text-xs text-zinc-500`}>{h.category}</td>
                        <td className={`${tdCls} text-right tnum text-zinc-300`}>{inr(h.invested_amount)}</td>
                        <td className={`${tdCls} text-right tnum text-zinc-100`}>{inr(h.current_value)}</td>
                        <td className={`${tdCls} text-right tnum font-semibold ${gainClass(h.return_pct)}`}>{pct(h.return_pct)}</td>
                        <td className={`${tdCls} text-right tnum text-zinc-400`}>{pct(h.allocation_pct, 1)}</td>
                        <td className={tdCls}><Badge variant="outline" className="text-[10px] border-white/10 text-zinc-400">{h.status}</Badge></td>
                        <td className={`${tdCls} text-right`}>
                          <button data-testid={`edit-holding-${h.id}`} onClick={() => setHoldingDlg({ ...h, asset_class: h.category })}
                            className="text-zinc-500 hover:text-emerald-300 transition-colors duration-200 mr-3"><PencilSimple size={15} /></button>
                          <button data-testid={`delete-holding-${h.id}`} onClick={() => deleteHolding(h)}
                            className="text-zinc-500 hover:text-red-400 transition-colors duration-200"><Trash size={15} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "Transactions" && (
          <div className="glass-card overflow-hidden" data-testid="panel-transactions">
            <div className="px-6 pt-5 pb-4 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-white">Transactions</h3>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" data-testid="admin-txn-statement"
                  onClick={() => downloadStatement(`/statements/${id}/transactions.pdf${txnStmtQuery()}`, `KMC-Transaction-Statement-${profile.investor_code}.pdf`)}
                  className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
                  <FileArrowDown size={14} className="mr-1" /> Statement (PDF)
                </Button>
                <Button size="sm" data-testid="add-transaction-button" onClick={() => setTxnDlg(true)}
                  className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
                  <Plus size={14} className="mr-1" weight="bold" /> Record Transaction
                </Button>
              </div>
            </div>
            <div className="px-6 pb-4">
              <TxnPeriodFilter mode={txnMode} setMode={setTxnMode} from={txnFrom} setFrom={setTxnFrom}
                to={txnTo} setTo={setTxnTo} onApply={applyTxnPeriod}
                onReset={() => { setTxnMode("All"); setTxnFrom(""); setTxnTo(""); }} testId="client-txn-period" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-client-txn-table">
                <thead>
                  <tr className="border-y border-white/[0.06]">
                    <th className={thCls}>Date</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Description</th>
                    <th className={thCls}>Reference</th>
                    <th className={`${thCls} text-right`}>Amount</th>
                    <th className={`${thCls} text-right`}>Status</th>
                    <th className={`${thCls} text-right`}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.filter((x) => inRange(x.date, txnMode, txnFrom, txnTo)).map((t) => (
                    <tr key={t.id} className={rowCls}>
                      <td className={`${tdCls} tnum text-zinc-300 whitespace-nowrap`}>{fmtDate(t.date)}</td>
                      <td className={`${tdCls} text-xs font-semibold text-zinc-300`}>
                        {t.type}
                        {(t.withdrawal_type || t.fee_type) && (
                          <p className="text-[10px] text-zinc-500 font-normal mt-0.5">
                            {t.withdrawal_type ? `${t.withdrawal_type} Withdrawal` : t.fee_type}
                          </p>
                        )}
                        {t.early_withdrawal ? <p className="text-[10px] text-amber-300 font-normal">Early · Exit fee {inr(t.exit_fee_amount || 0)}</p> : null}
                        {t.profit_sharing_amount ? <p className="text-[10px] text-zinc-500 font-normal">Profit share {inr(t.profit_sharing_amount)}</p> : null}
                        {t.reversed_by ? <p className="text-[10px] text-red-400 font-normal">Reversed</p> : null}
                      </td>
                      <td className={`${tdCls} text-zinc-400`}>{t.description}</td>
                      <td className={`${tdCls} tnum text-[11px] text-zinc-600`}>{t.reference_number || "—"}</td>
                      <td className={`${tdCls} text-right tnum text-zinc-100`}>{inr(t.amount)}</td>
                      <td className={`${tdCls} text-right`}><Badge variant="outline" className={`text-[10px] ${txnBadge(t.status)}`}>{t.status}</Badge></td>
                      <td className={`${tdCls} text-right`}>
                        {t.type !== "Reversal" && !t.reversed_by && (
                          <button data-testid={`client-txn-edit-${t.id}`} onClick={() => setEditTxn(t)}
                            className="text-[11px] font-semibold text-zinc-500 hover:text-emerald-300 transition-colors duration-200">
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "Performance" && (
          <div className="space-y-5" data-testid="panel-performance">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
              <StatCard label="Invested Value" value={inr(metrics.total_invested)} testId="pm-invested" />
              <StatCard label="Current Value" value={inr(metrics.current_value)} testId="pm-current" />
              <StatCard label="Absolute Return" value={inr(metrics.gain_loss)} delta={metrics.gain_loss_pct} testId="pm-abs" />
              <StatCard label="CAGR" value={metrics.cagr !== null ? pct(metrics.cagr) : "—"} testId="pm-cagr" />
              <StatCard label="XIRR" value={metrics.xirr !== null ? pct(metrics.xirr) : "—"} testId="pm-xirr" />
            </div>
            <div className="glass-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-heading font-semibold text-white">Portfolio Value Over Time</h3>
                <PeriodSelector period={pmPeriod} setPeriod={setPmPeriod} testId="pm-period-selector" />
              </div>
              {pmData.length > 1 ? (
                <GrowthArea data={pmData} height={320} testId="pm-growth-chart" investedKey="invested" />
              ) : (
                <EmptyState message="Insufficient history for this range. Try a longer period." />
              )}
            </div>
          </div>
        )}

        {tab === "Documents" && (
          <div className="glass-card overflow-hidden" data-testid="panel-documents">
            <div className="px-6 pt-5 pb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-heading font-semibold text-white">Secure Documents</h3>
              <div className="flex items-center gap-2">
                <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} data-testid="upload-category"
                  className="glass-input h-9 w-44 text-xs appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                  {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                  data-testid="upload-file-input" onChange={upload} />
                <Button size="sm" data-testid="upload-document-button" onClick={() => fileRef.current?.click()}
                  className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
                  <UploadSimple size={14} className="mr-1" weight="bold" /> Upload
                </Button>
              </div>
            </div>
            {documents.length === 0 ? <EmptyState message="No documents uploaded yet." /> : documents.map((d) => (
              <div key={d.id} className="px-6 py-3.5 border-t border-white/[0.04] flex items-center gap-4" data-testid={`admin-doc-row-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{d.name}</p>
                  <p className="text-[11px] text-zinc-500">{d.category} · {fmtDate(d.uploaded_at)} · {d.size_kb} KB · {d.uploaded_by}</p>
                </div>
                <button onClick={() => downloadDoc(d)} data-testid={`admin-doc-download-${d.id}`}
                  className="h-8 w-8 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors duration-200">
                  <Download size={14} />
                </button>
                <button onClick={() => deleteDoc(d)} data-testid={`admin-doc-delete-${d.id}`}
                  className="h-8 w-8 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-red-400 hover:border-red-500/30 transition-colors duration-200">
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "Nominee" && (
          <div className="glass-card p-7 max-w-3xl" data-testid="panel-nominee">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <In label="Nominee Name" value={form.nominee?.name} onChange={setNom("name")} testId="e-nom-name" />
              <In label="Relationship" value={form.nominee?.relation} onChange={setNom("relation")} testId="e-nom-relation" />
              <In label="Date of Birth" type="date" value={form.nominee?.dob} onChange={setNom("dob")} testId="e-nom-dob" />
              <In label="Mobile" value={form.nominee?.mobile} onChange={setNom("mobile")} testId="e-nom-mobile" />
              <In label="Email" value={form.nominee?.email} onChange={setNom("email")} testId="e-nom-email" />
              <In label="Share %" type="number" value={form.nominee?.share_pct} onChange={(v) => setNom("share_pct")(Number(v))} testId="e-nom-share" />
            </div>
            <SaveBar testId="save-nominee" saving={saving} onSave={() => save({ nominee: form.nominee })} />
          </div>
        )}

        {tab === "Investment Plan" && (
          <div className="glass-card p-7 max-w-3xl" data-testid="panel-plan">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Sel label="Investor Type" value={form.investor_type} testId="e-investor-type"
                onChange={(v) => setForm((p) => ({ ...p, investor_type: v }))}
                options={["", ...INVESTOR_TYPE_NAMES]} />
              <div className="space-y-1.5">
                <label className="label-caps">Series (auto)</label>
                <p className="glass-input flex items-center text-sm text-emerald-300" data-testid="e-series-auto">
                  {form.investor_type ? `${INVESTOR_TYPES[form.investor_type].series} Series` : "—"}
                </p>
              </div>
              <Sel label="Tenure" value={form.tenure_category} onChange={set("tenure_category")}
                options={["", ...TENURE_OPTIONS]} testId="e-tenure" />
              <div className="space-y-1.5">
                <label className="label-caps">Applicable Plan (auto)</label>
                <p className="glass-input flex items-center text-sm text-emerald-300" data-testid="e-plan-auto">
                  {form.investor_type && form.tenure_category ? INVESTOR_TYPES[form.investor_type].plans[form.tenure_category] : "—"}
                </p>
              </div>
              <div className="sm:col-span-2">
                <In label="Investment Objective" value={form.investment_objective} onChange={set("investment_objective")} testId="e-objective" />
              </div>
              <Sel label="Withdrawal Option" value={form.withdrawal_preference} onChange={set("withdrawal_preference")}
                options={["", ...WITHDRAWAL_OPTIONS]} testId="e-withdrawal" />
            </div>
            <div className="mt-5 border-t border-white/[0.06] pt-5">
              <p className="label-caps mb-1">Withdrawal Rules — Client Overrides</p>
              <p className="text-[11px] text-zinc-600 mb-4">Blank uses the firm-wide defaults from Settings.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <In label="Exit Load %" type="number" value={form.exit_load_pct} onChange={set("exit_load_pct")} testId="e-exit-load" />
                <In label="Profit Sharing %" type="number" value={form.profit_sharing_pct} onChange={set("profit_sharing_pct")} testId="e-profit-sharing" />
                <In label="Capital Lock (calendar days)" type="number" value={form.capital_lock_days} onChange={set("capital_lock_days")} testId="e-lock-days" />
                <Sel label="Partial Withdrawal" value={form.allow_partial_withdrawal} onChange={set("allow_partial_withdrawal")} options={["", "Yes", "No"]} testId="e-allow-partial" />
                <Sel label="Full Redemption" value={form.allow_full_redemption} onChange={set("allow_full_redemption")} options={["", "Yes", "No"]} testId="e-allow-full" />
              </div>
            </div>
            {form.investor_type && (
              <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] p-4" data-testid="e-range-panel">
                <p className="label-caps">Indicative Return Range</p>
                <p className="tnum text-emerald-300 font-semibold text-base mt-2" data-testid="e-range">
                  {INVESTOR_TYPES[form.investor_type].range}
                  <span className="text-zinc-500 font-normal text-xs"> · Risk appetite: {INVESTOR_TYPES[form.investor_type].riskAppetite}</span>
                </p>
                <p className="text-[11px] text-zinc-500 mt-1.5">{RANGE_DISCLAIMER}</p>
                {data?.profile?.maturity_date && (
                  <p className="text-[11px] text-zinc-500 mt-1.5 tnum" data-testid="e-maturity">Maturity date: {fmtDate(data.profile.maturity_date)}</p>
                )}
              </div>
            )}
            <SaveBar testId="save-plan" saving={saving} onSave={() => save({
              investor_type: form.investor_type || null, tenure_category: form.tenure_category || null,
              investment_objective: form.investment_objective,
              withdrawal_preference: form.withdrawal_preference || null,
              exit_load_pct: form.exit_load_pct === "" ? null : Number(form.exit_load_pct),
              profit_sharing_pct: form.profit_sharing_pct === "" ? null : Number(form.profit_sharing_pct),
              capital_lock_days: form.capital_lock_days === "" ? null : Number(form.capital_lock_days),
              allow_partial_withdrawal: form.allow_partial_withdrawal === "" ? null : form.allow_partial_withdrawal === "Yes",
              allow_full_redemption: form.allow_full_redemption === "" ? null : form.allow_full_redemption === "Yes",
            })} />
          </div>
        )}

        {tab === "Valuations" && (
          <div className="glass-card overflow-hidden" data-testid="panel-valuations">
            <div className="px-6 pt-5 pb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-heading font-semibold text-white">Portfolio Valuations</h3>
                <p className="text-xs text-zinc-500 mt-1">Monthly or weekly portfolio values that drive the performance graph. Valuations are records — they never create transactions.</p>
              </div>
              <Button size="sm" data-testid="add-valuation"
                onClick={() => setValDlg({ month: "", period: "monthly", date: "", opening_value: "", investment_added: "", withdrawal: "", value: "", notes: "" })}
                className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
                <Plus size={14} className="mr-1" weight="bold" /> Add Valuation
              </Button>
            </div>
            {history.length === 0 ? <EmptyState message="No valuation data recorded yet." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="valuations-table">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className={thCls}>Month</th>
                      <th className={thCls}>Period</th>
                      <th className={thCls}>Valuation Date</th>
                      <th className={`${thCls} text-right`}>Opening</th>
                      <th className={`${thCls} text-right`}>Added</th>
                      <th className={`${thCls} text-right`}>Withdrawn</th>
                      <th className={`${thCls} text-right`}>Closing Value</th>
                      <th className={`${thCls} text-right`}>Perf %</th>
                      <th className={thCls}>Notes</th>
                      <th className={`${thCls} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...history].sort((a, b) => (b.month || "").localeCompare(a.month || "")).map((h) => (
                      <tr key={h.id} className={rowCls} data-testid={`valuation-row-${h.id}`}>
                        <td className={`${tdCls} tnum text-xs text-zinc-300`}>{h.month}</td>
                        <td className={`${tdCls} text-[10px] font-semibold ${h.period === "weekly" ? "text-yellow-300" : "text-zinc-500"}`}>{(h.period || "monthly").toUpperCase()}</td>
                        <td className={`${tdCls} tnum text-xs text-zinc-400 whitespace-nowrap`}>{fmtDate(h.date)}</td>
                        <td className={`${tdCls} text-right tnum text-zinc-400`}>{h.opening_value != null ? inr(h.opening_value) : "—"}</td>
                        <td className={`${tdCls} text-right tnum text-zinc-400`}>{h.investment_added != null ? inr(h.investment_added) : "—"}</td>
                        <td className={`${tdCls} text-right tnum text-zinc-400`}>{h.withdrawal != null ? inr(h.withdrawal) : "—"}</td>
                        <td className={`${tdCls} text-right tnum text-zinc-100`}>{inr(h.value)}</td>
                        <td className={`${tdCls} text-right tnum ${h.performance_pct != null ? (h.performance_pct >= 0 ? "text-emerald-300" : "text-red-400") : "text-zinc-600"}`}>
                          {h.performance_pct != null ? pct(h.performance_pct) : "—"}
                        </td>
                        <td className={`${tdCls} text-[11px] text-zinc-500 max-w-[180px] truncate`}>{h.notes || "—"}</td>
                        <td className={`${tdCls} text-right whitespace-nowrap`}>
                          <button data-testid={`valuation-edit-${h.id}`} onClick={() => setValDlg({
                            id: h.id, month: h.month, period: h.period || "monthly", date: (h.date || "").slice(0, 10),
                            opening_value: h.opening_value ?? "", investment_added: h.investment_added ?? "",
                            withdrawal: h.withdrawal ?? "", value: h.value, notes: h.notes || "",
                          })} className="text-[11px] font-semibold text-zinc-500 hover:text-emerald-300 mr-3 transition-colors duration-200">Edit</button>
                          <button data-testid={`valuation-delete-${h.id}`} onClick={() => removeValuation(h)}
                            className="text-[11px] font-semibold text-zinc-500 hover:text-red-400 transition-colors duration-200">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "Notes" && (
          <div className="glass-card p-7" data-testid="panel-notes">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[["Internal Notes", "internal", "e-notes-internal"], ["Client Preferences", "preferences", "e-notes-prefs"],
                ["Operational Notes", "operational", "e-notes-ops"], ["Special Instructions", "special_instructions", "e-notes-special"]].map(([label, key, tid]) => (
                <div key={key} className="space-y-1.5">
                  <label className="label-caps">{label}</label>
                  <textarea rows={5} value={form.notes?.[key]} onChange={(e) => setNote(key)(e.target.value)}
                    data-testid={tid} className="glass-input h-auto py-2.5 resize-none" />
                </div>
              ))}
            </div>
            <SaveBar testId="save-notes" saving={saving} onSave={() => save({ notes: form.notes })} />
          </div>
        )}

        {tab === "Activity" && (
          <div className="glass-card overflow-hidden" data-testid="panel-activity">
            <div className="px-6 pt-5 pb-4">
              <h3 className="font-heading font-semibold text-white">Audit Trail</h3>
              <p className="text-xs text-zinc-500 mt-1">Every sensitive change to this client record, with old and new values.</p>
            </div>
            {activity.length === 0 ? <EmptyState message="No activity recorded yet." /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="client-activity-table">
                  <thead>
                    <tr className="border-y border-white/[0.06]">
                      <th className={thCls}>Timestamp</th>
                      <th className={thCls}>Actor</th>
                      <th className={thCls}>Action</th>
                      <th className={thCls}>Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map((a) => (
                      <tr key={a.id} className={rowCls}>
                        <td className={`${tdCls} tnum text-xs text-zinc-400 whitespace-nowrap`}>{fmtDateTime(a.timestamp)}</td>
                        <td className={`${tdCls} text-xs text-zinc-300`}>{a.actor_email}</td>
                        <td className={tdCls}><Badge variant="outline" className="text-[10px] border-emerald-500/25 bg-emerald-500/10 text-emerald-300">{a.action}</Badge></td>
                        <td className={`${tdCls} text-[11px] text-zinc-500 max-w-md`}>
                          {(a.details?.changes || []).map((c, i) => (
                            <div key={i} className="truncate">
                              <span className="text-zinc-300">{c.field}</span>: {JSON.stringify(c.old) ?? "—"} → {JSON.stringify(c.new)}
                            </div>
                          )) || JSON.stringify(a.details)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={codeDlg} onOpenChange={setCodeDlg}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-md" data-testid="client-code-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">Edit Client Code</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Client Code must be unique across all clients. The change is audit logged and reflected in all portals.
            </DialogDescription>
          </DialogHeader>
          <input value={codeValue} onChange={(e) => setCodeValue(e.target.value.toUpperCase())}
            data-testid="client-code-input" className="glass-input h-11 tnum" placeholder="KMC-INV-1001" />
          <Button onClick={saveCode} disabled={saving} data-testid="client-code-save"
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
            {saving ? "Saving..." : "Save Client Code"}
          </Button>
        </DialogContent>
      </Dialog>

      <HoldingDialog state={holdingDlg} setState={setHoldingDlg} onSave={saveHolding} />
      <TxnDialog open={txnDlg} setOpen={setTxnDlg} onSave={saveTxn}
        holdings={data?.holdings || []} txns={data?.transactions || []} profile={data?.profile || {}} />
      <ValuationDialog state={valDlg} setState={setValDlg} onSave={saveValuation} />
      <TxnEditDialog txn={editTxn} onClose={() => setEditTxn(null)}
        onSaved={() => { setEditTxn(null); load(); }} />
    </AppLayout>
  );
}

function HoldingDialog({ state, setState, onSave }) {
  if (!state) return null;
  const s = (k) => (v) => setState((p) => ({ ...p, [k]: v }));
  const num = (v) => (v === "" ? null : Number(v));
  return (
    <Dialog open={!!state} onOpenChange={() => setState(null)}>
      <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-2xl" data-testid="holding-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">{state.id ? "Edit Holding" : "Add Holding"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><In label="Asset Name" value={state.asset_name} onChange={s("asset_name")} testId="h-asset-name" /></div>
          <Sel label="Asset Class" value={state.asset_class} onChange={s("asset_class")} options={ASSET_CLASSES} testId="h-asset-class" />
          <In label="Investment Date" type="date" value={state.investment_date} onChange={s("investment_date")} testId="h-inv-date" />
          <In label="Invested Amount (₹)" type="number" value={state.invested_amount} onChange={s("invested_amount")} testId="h-invested" />
          <In label="Current Value (₹)" type="number" value={state.current_value} onChange={s("current_value")} testId="h-current" />
          <In label="Units" type="number" value={state.units} onChange={s("units")} testId="h-units" />
          <In label="Purchase Price" type="number" value={state.purchase_price} onChange={s("purchase_price")} testId="h-purchase" />
          <In label="Current Price" type="number" value={state.current_price} onChange={s("current_price")} testId="h-current-price" />
          <Sel label="Status" value={state.status} onChange={s("status")} options={["Active", "Closed", "On Hold"]} testId="h-status" />
        </div>
        <Button data-testid="holding-save" onClick={() => onSave({
          ...state, invested_amount: Number(state.invested_amount), current_value: Number(state.current_value),
          units: num(state.units), purchase_price: num(state.purchase_price), current_price: num(state.current_price),
        })} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold mt-4">
          {state.id ? "Update Holding" : "Add Holding"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function TxnDialog({ open, setOpen, onSave, holdings = [], txns = [], profile = {} }) {
  const [t, setT] = useState({ date: "", type: "Investment", description: "", amount: "", reference_number: "",
    status: "Completed", remarks: "", withdrawal_type: "", holding_id: "", fee_type: "",
    profit_sharing_pct: "", exit_fee_pct: "", exit_fee_override: false, record_charge: true });
  const [quote, setQuote] = useState(null);
  const [feeQuote, setFeeQuote] = useState(null);
  const [wsim, setWsim] = useState(null);
  const s = (k) => (v) => setT((p) => ({ ...p, [k]: v }));
  const amt = Number(t.amount) || 0;
  const tenureDigits = (profile.investment_tenure || "").match(/\d+/);
  const capDates = txns.filter((x) => ["Investment", "Additional Investment"].includes(x.type))
    .map((x) => new Date(x.date)).sort((a, b) => a - b);
  const maturity = capDates.length && tenureDigits
    ? new Date(capDates[0].getTime() + 365.25 * 86400000 * Math.max(1, parseInt(tenureDigits[0], 10))) : null;
  const txnDate = t.date ? new Date(t.date) : null;
  const early = Boolean(maturity && txnDate && txnDate < maturity);
  const psAmt = t.withdrawal_type === "Return" && Number(t.profit_sharing_pct) ? (amt * Number(t.profit_sharing_pct)) / 100 : 0;
  const efAmt = t.withdrawal_type === "Capital" && Number(t.exit_fee_pct) ? (amt * Number(t.exit_fee_pct)) / 100 : 0;

  useEffect(() => {
    if (t.type === "Additional Investment" && amt > 0 && profile.id) {
      const h = setTimeout(() => api.get(`/admin/investors/${profile.id}/fee-quote`, { params: { kind: "purchase", amount: amt } })
        .then((r) => setQuote(r.data)).catch(() => setQuote(null)), 400);
      return () => clearTimeout(h);
    }
    setQuote(null);
  }, [t.type, amt, profile.id]);

  useEffect(() => {
    if (t.type === "Fee" && t.fee_type === "Onboarding Fee" && profile.id) {
      api.get(`/admin/investors/${profile.id}/fee-quote`, { params: { kind: "onboarding" } })
        .then((r) => {
          setFeeQuote(r.data);
          setT((p) => (p.amount === "" || p.amount === "0" ? { ...p, amount: r.data.fee_amount } : p));
        }).catch(() => setFeeQuote(null));
    } else {
      setFeeQuote(null);
    }
  }, [t.type, t.fee_type, profile.id]);

  useEffect(() => {
    if (t.type === "Withdrawal" && t.withdrawal_type && amt > 0 && profile.id) {
      const h = setTimeout(() => api.post("/withdrawal/simulate", {
        investor_id: profile.id, withdrawal_type: t.withdrawal_type, amount: amt,
        profit_sharing_pct: t.withdrawal_type === "Return" && t.profit_sharing_pct !== "" ? Number(t.profit_sharing_pct) : null,
      }).then((r) => setWsim(r.data)).catch(() => setWsim(null)), 400);
      return () => clearTimeout(h);
    }
    setWsim(null);
  }, [t.type, t.withdrawal_type, amt, t.profit_sharing_pct, profile.id]);

  const submit = () => onSave({
    ...t, amount: amt,
    withdrawal_type: t.withdrawal_type || null, holding_id: t.holding_id || null, fee_type: t.fee_type || null,
    profit_sharing_pct: t.withdrawal_type === "Return" && t.profit_sharing_pct !== "" ? Number(t.profit_sharing_pct) : null,
    exit_fee_pct: t.withdrawal_type === "Capital" && t.exit_fee_pct !== "" ? Number(t.exit_fee_pct) : null,
  }, t.type === "Additional Investment" && t.record_charge && quote ? quote.fee_amount : null);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-xl" data-testid="txn-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Record Transaction</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Fields adapt to the transaction type. Withdrawal classification drives invested-capital, exit-fee and profit-sharing calculations.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <In label="Date" type="date" value={t.date} onChange={s("date")} testId="t-date" />
          <div className="space-y-1.5">
            <label className="label-caps">Transaction Type</label>
            <select value={t.type} data-testid="t-type"
              onChange={(e) => setT((p) => ({ ...p, type: e.target.value, withdrawal_type: "", fee_type: "", profit_sharing_pct: "", exit_fee_pct: "", exit_fee_override: false }))}
              className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
              {[["Investment", "Capital Deployed / Initial Investment"], ["Additional Investment", "Additional Purchase"],
                ["Withdrawal", "Withdrawal"], ["Fee", "Fee"], ["Dividend", "Dividend"], ["Interest", "Interest"],
                ["Other", "Other"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><In label="Description" value={t.description} onChange={s("description")} testId="t-description" /></div>
          <In label="Amount (₹)" type="number" value={t.amount} onChange={s("amount")} testId="t-amount" />
          <In label="Reference Number" value={t.reference_number} onChange={s("reference_number")} testId="t-reference" />
          {t.type === "Additional Investment" && quote && (
            <div className="sm:col-span-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="t-purchase-charge">
              <label className="flex flex-wrap items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={t.record_charge} onChange={(e) => s("record_charge")(e.target.checked)}
                  data-testid="t-record-charge" className="accent-emerald-500" />
                <span>Record purchase charge <span className="tnum text-emerald-300 font-semibold">{inr(quote.fee_amount)}</span> ({quote.rate_pct}% slab on {inr(quote.base)}) as a fee transaction</span>
              </label>
            </div>
          )}
          {t.type === "Withdrawal" && (
            <>
              <Sel label="Withdrawal Type" value={t.withdrawal_type} onChange={s("withdrawal_type")}
                options={["", "Capital", "Return"]} testId="t-withdrawal-type" />
              <div className="space-y-1.5">
                <label className="label-caps">Holding (optional)</label>
                <select value={t.holding_id} onChange={(e) => s("holding_id")(e.target.value)} data-testid="t-holding"
                  className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
                  <option value="">Portfolio level</option>
                  {holdings.map((h) => <option key={h.id} value={h.id}>{h.asset_name}</option>)}
                </select>
              </div>
              {t.withdrawal_type === "Return" && (
                <>
                  <In label="Profit Sharing %" type="number" value={t.profit_sharing_pct} onChange={s("profit_sharing_pct")} testId="t-profit-sharing" />
                  <div className="space-y-1.5">
                    <label className="label-caps">Profit Sharing Amount</label>
                    <p className="tnum text-lg text-emerald-300 pt-1.5" data-testid="t-profit-sharing-amount">{inr(psAmt)}</p>
                  </div>
                  <p className="sm:col-span-2 text-[11px] text-zinc-600" data-testid="t-exit-na">Exit Load: Not Applicable for return withdrawals.</p>
                </>
              )}
              {t.withdrawal_type === "Capital" && (
                <>
                  <div className="sm:col-span-2 text-[11px] leading-relaxed rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-zinc-500" data-testid="t-maturity-note">
                    {maturity
                      ? (early
                        ? <>Early withdrawal — before maturity ({fmtDate(maturity)}). Exit load may apply.</>
                        : <>On/after maturity ({fmtDate(maturity)}). Exit load not applicable unless overridden.</>)
                      : "Maturity cannot be determined (no capital deployment date or tenure on record)."}
                  </div>
                  <In label="Exit Fee / Exit Load %" type="number" value={t.exit_fee_pct} onChange={s("exit_fee_pct")} testId="t-exit-fee" />
                  <div className="space-y-1.5">
                    <label className="label-caps">Exit Fee Amount</label>
                    <p className="tnum text-lg text-emerald-300 pt-1.5" data-testid="t-exit-fee-amount">{inr(efAmt)}</p>
                  </div>
                  {!early && maturity && (
                    <label className="sm:col-span-2 flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                      <input type="checkbox" checked={t.exit_fee_override}
                        onChange={(e) => s("exit_fee_override")(e.target.checked)} data-testid="t-exit-override" className="accent-emerald-500" />
                      Override maturity rule (recorded in audit log)
                    </label>
                  )}
                  <p className="sm:col-span-2 text-[11px] text-zinc-600" data-testid="t-ps-na">Profit Sharing: Not Applicable for capital withdrawals.</p>
                </>
              )}
            </>
          )}
          {t.type === "Fee" && (
            <>
              <Sel label="Fee Type" value={t.fee_type} onChange={s("fee_type")}
                options={["", "Onboarding Fee", "Exit Fee", "Profit Sharing", "Purchase Charge", "Other Fee"]} testId="t-fee-type" />
              {t.fee_type === "Onboarding Fee" && feeQuote && (
                <p className="sm:col-span-2 text-[11px] text-zinc-500" data-testid="t-onboarding-quote">
                  Slab rate {feeQuote.rate_pct}% on invested AUM {inr(feeQuote.base)} → fee auto-filled as {inr(feeQuote.fee_amount)} (editable, snapshot stored for audit).
                </p>
              )}
            </>
          )}
          {t.type === "Withdrawal" && wsim && (
            <div className="sm:col-span-2 rounded-lg border border-emerald-500/15 bg-emerald-500/[0.04] px-4 py-3 space-y-2" data-testid="t-withdrawal-preview">
              <div className={`text-xs font-semibold ${wsim.eligible ? "text-emerald-300" : "text-amber-300"}`} data-testid="t-preview-eligibility">
                {wsim.eligible ? "Eligible under current withdrawal rules." : wsim.reason}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-[11px] text-zinc-400 tnum">
                <span>Interest earned: <b className="text-white">{inr(wsim.withdrawal?.earned_profit || 0)}</b></span>
                <span>Max withdrawable from interest: <b className="text-white">{inr(wsim.withdrawal?.withdrawable_profit || 0)}</b></span>
                <span>Gross: <b className="text-white">{inr(wsim.breakdown.gross)}</b></span>
                <span>Exit load ({wsim.breakdown.exit_load_pct}%): <b className="text-white">{inr(wsim.breakdown.exit_load_amount)}</b></span>
                <span>Profit share ({wsim.breakdown.profit_sharing_pct}%): <b className="text-white">{inr(wsim.breakdown.profit_sharing_amount)}</b></span>
                <span>Net payable: <b className="text-emerald-300">{inr(wsim.breakdown.net_payable)}</b></span>
              </div>
            </div>
          )}
          <Sel label="Status" value={t.status} onChange={s("status")} options={["Completed", "Pending", "Failed"]} testId="t-status" />
          <In label="Remarks" value={t.remarks} onChange={s("remarks")} testId="t-remarks" />
        </div>
        <Button data-testid="txn-save" onClick={submit}
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold mt-4">
          Record Transaction
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ValuationDialog({ state, setState, onSave }) {
  if (!state) return null;
  const s = (k) => (v) => setState((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open={!!state} onOpenChange={() => setState(null)}>
      <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-xl" data-testid="valuation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">{state.id ? "Edit Valuation" : "Add Portfolio Valuation"}</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Monthly or weekly portfolio valuation record — tracks investment growth on the performance graph. Valuations never create or modify transactions.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Sel label="Period" value={state.period || "monthly"} onChange={s("period")} options={["monthly", "weekly"]} testId="v-period" />
          {state.period !== "weekly" && <In label="Month *" type="month" value={state.month} onChange={s("month")} testId="v-month" />}
          <In label={state.period === "weekly" ? "Valuation Date *" : "Valuation Date"} type="date" value={state.date} onChange={s("date")} testId="v-date" />
          <In label="Opening Portfolio Value (₹)" type="number" value={state.opening_value} onChange={s("opening_value")} testId="v-opening" />
          <In label="Investment Added (₹)" type="number" value={state.investment_added} onChange={s("investment_added")} testId="v-added" />
          <In label="Withdrawal (₹)" type="number" value={state.withdrawal} onChange={s("withdrawal")} testId="v-withdrawal" />
          <In label="Closing Portfolio Value (₹) *" type="number" value={state.value} onChange={s("value")} testId="v-closing" />
          <div className="sm:col-span-2"><In label="Notes" value={state.notes} onChange={s("notes")} testId="v-notes" /></div>
        </div>
        <Button data-testid="valuation-save" onClick={() => onSave(state)}
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold mt-4">
          {state.id ? "Update Valuation" : "Save Valuation"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
